/**
 * <gx-buy-block>
 *
 * Product-page purchase card: name + price, quantity, and a live total
 * (price x quantity) that recalculates instantly, with no reload, on two
 * triggers:
 *   1. the quantity input's `change` event (fired by Dawn's own
 *      <quantity-input> component in global.js when +/- is used)
 *   2. Dawn's global `variant-change` pub/sub event (fired by
 *      product-info.js whenever the variant picker switches variant)
 *
 * "Ajouter au panier" is a plain submit button inside Dawn's own
 * {% form 'product' %} wrapped in <product-form> — Dawn's product-form.js
 * progressively enhances it exactly like any other Dawn add-to-cart button
 * (AJAX add, cart drawer/notification, error handling), no extra code needed.
 *
 * "Acheter maintenant" is deliberately NOT Shopify's native dynamic-checkout
 * button (`payment_button`): that one renders Shopify's own payment icons
 * and can't be restyled to a plain custom button. Instead this adds to cart
 * through the same endpoint Dawn's own form uses, then redirects straight to
 * /checkout, reproducing the same end result with our own markup/styling.
 */
class GxBuyBlock extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('form');
    this.quantityInput = this.querySelector('.quantity__input');
    this.priceEl = this.querySelector('.gx-buy-block__price');
    this.totalEl = this.querySelector('.gx-buy-block__total-value');
    this.variantIdInput = this.querySelector('.product-variant-id');
    this.addButton = this.querySelector('.gx-buy-block__button--add');
    this.buyNowButton = this.querySelector('.gx-buy-block__button--buy-now');
    this.errorWrapper = this.querySelector('.product-form__error-message-wrapper');
    this.errorMessage = this.errorWrapper ? this.errorWrapper.querySelector('.product-form__error-message') : null;

    this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    this.unitPrice = Number(this.dataset.unitPrice) || 0;

    if (this.quantityInput) {
      this.quantityInput.addEventListener('change', this.clampQuantity.bind(this));
      this.quantityInput.addEventListener('change', this.updateTotal.bind(this));
    }
    if (this.buyNowButton) this.buyNowButton.addEventListener('click', this.onBuyNowClick.bind(this));
  }

  connectedCallback() {
    this.updateTotal();
    this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));
    // Dawn's own product-info.js resets the quantity input's value (e.g. back
    // to the minimum after an add-to-cart) by setting `.value` directly, with
    // no `change` event — it publishes `quantityUpdate` instead. Without this,
    // the total goes stale after that kind of reset.
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.updateTotal.bind(this));
  }

  disconnectedCallback() {
    if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
    if (this.quantityUpdateUnsubscriber) this.quantityUpdateUnsubscriber();
  }

  get quantity() {
    return parseInt(this.quantityInput && this.quantityInput.value, 10) || 1;
  }

  // The +/- buttons (Dawn's own <quantity-input> component) already respect
  // min/max via stepUp()/stepDown(), but nothing stops someone from typing
  // a number straight into the field -- the `max` attribute alone doesn't
  // block that (our form uses novalidate, like Dawn's own buy-buttons.liquid,
  // so native validation never kicks in either). Clamp it by hand instead.
  clampQuantity() {
    if (!this.quantityInput) return;
    const min = parseInt(this.quantityInput.min, 10);
    const max = parseInt(this.quantityInput.max, 10);
    let value = parseInt(this.quantityInput.value, 10);
    if (Number.isNaN(value)) return;
    if (!Number.isNaN(min) && value < min) value = min;
    if (!Number.isNaN(max) && value > max) value = max;
    this.quantityInput.value = value;
  }

  updateTotal() {
    if (this.totalEl) this.totalEl.textContent = this.formatMoney(this.unitPrice * this.quantity);
  }

  onVariantChange(event) {
    const variant = event.data.variant;
    const available = Boolean(variant) && variant.available !== false;

    if (this.variantIdInput) this.variantIdInput.value = variant ? variant.id : '';

    if (variant) {
      this.unitPrice = variant.price;
      if (this.priceEl) this.priceEl.textContent = this.formatMoney(this.unitPrice);
    }

    this.updateTotal();
    this.toggleAvailability(available, variant);
  }

  toggleAvailability(available, variant) {
    [this.addButton, this.buyNowButton].forEach((button) => {
      if (button) button.disabled = !available;
    });

    if (!this.addButton) return;
    const label = this.addButton.querySelector('span');
    if (!label || !window.variantStrings) return;

    if (!variant) label.textContent = window.variantStrings.unavailable;
    else if (!available) label.textContent = window.variantStrings.soldOut;
    else label.textContent = window.variantStrings.addToCart;
  }

  async onBuyNowClick() {
    if (this.buyNowButton.disabled || !this.form) return;

    this.setLoading(true);
    this.showError(false);

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    config.body = new FormData(this.form);

    try {
      const response = await fetch(window.routes.cart_add_url, config);
      const result = await response.json();

      if (result.status) {
        this.showError(result.description || result.message);
        this.setLoading(false);
        return;
      }

      window.location.href = '/checkout';
    } catch (error) {
      this.showError(window.cartStrings && window.cartStrings.error);
      this.setLoading(false);
    }
  }

  setLoading(loading) {
    this.buyNowButton.classList.toggle('loading', loading);
    const spinner = this.buyNowButton.querySelector('.loading__spinner');
    if (spinner) spinner.classList.toggle('hidden', !loading);
  }

  showError(message) {
    if (!this.errorWrapper) return;
    this.errorWrapper.toggleAttribute('hidden', !message);
    if (message && this.errorMessage) this.errorMessage.textContent = message;
  }

  // Standard Shopify money formatter: applies the shop's own money_format
  // string (e.g. "{{amount}} DH") to a cents value, so the live total always
  // matches the store's real currency formatting.
  formatMoney(cents) {
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const format = this.moneyFormat;
    const match = format.match(placeholderRegex);
    const kind = match ? match[1] : 'amount';

    const withDelimiters = (number, precision, thousands, decimal) => {
      if (isNaN(number) || number === null) return '0';
      const fixed = (number / 100).toFixed(precision);
      const parts = fixed.split('.');
      const wholePart = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      const decimalPart = parts[1] ? decimal + parts[1] : '';
      return wholePart + decimalPart;
    };

    let value;
    switch (kind) {
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = withDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = withDelimiters(cents, 0, ' ', '');
        break;
      default:
        value = withDelimiters(cents, 2, ',', '.');
    }

    return match ? format.replace(placeholderRegex, value) : value;
  }
}

if (!customElements.get('gx-buy-block')) {
  customElements.define('gx-buy-block', GxBuyBlock);
}

/**
 * Gx price line under the title (gx-product-price snippet). Not a custom
 * element -- it's a plain <p>, separate from <gx-buy-block> in the DOM --
 * so it's just wired up here directly: read its starting price, then keep
 * it in sync with Dawn's global `variant-change` event, same as the card.
 */
function formatGxPrice(cents) {
  const whole = Math.floor(cents / 100);
  const decimals = String(cents % 100).padStart(2, '0');
  // en-US grouping gives comma-every-3-digits with no decimals of its own
  // (whole is already an integer), matching the Liquid-side formatting.
  const wholeGrouped = whole.toLocaleString('en-US');
  return `${wholeGrouped}.${decimals} DH`;
}

document.querySelectorAll('.gx-product-price').forEach((priceEl) => {
  const compareAtEl = priceEl.parentElement.querySelector('.gx-product-price-compare-at');

  subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
    const variant = event.data.variant;
    if (!variant) return;

    priceEl.textContent = formatGxPrice(variant.price);

    if (!compareAtEl) return;
    const onSale = variant.compare_at_price > variant.price;
    compareAtEl.classList.toggle('hidden', !onSale);
    compareAtEl.textContent = onSale ? formatGxPrice(variant.compare_at_price) : '';
  });
});

/**
 * Native share button (gx-share__button--native): the Web Share API where
 * supported (mobile browsers mostly -- opens the phone's own share sheet:
 * Messages, Telegram, Instagram, etc.), falling back to copying the product
 * link to the clipboard elsewhere (most desktop browsers) with a small
 * "Lien copié !" tooltip, so the button always does something useful
 * instead of sometimes not being there at all.
 */
document.querySelectorAll('.gx-share__button--native').forEach((button) => {
  button.addEventListener('click', async () => {
    const title = button.dataset.productTitle;
    const url = button.dataset.productUrl;

    if (navigator.share) {
      try {
        await navigator.share({ title, url });
      } catch (error) {
        // AbortError when the visitor just closes the share sheet -- not a real error.
      }
      return;
    }

    try {
      await navigator.clipboard.writeText(url);
      showGxShareTooltip(button, 'Lien copié !');
    } catch (error) {
      // Clipboard API unavailable/blocked -- nothing more we can do here.
    }
  });
});

function showGxShareTooltip(button, message) {
  const existing = button.querySelector('.gx-share__tooltip');
  if (existing) existing.remove();

  const tooltip = document.createElement('span');
  tooltip.className = 'gx-share__tooltip';
  tooltip.textContent = message;
  button.appendChild(tooltip);
  setTimeout(() => tooltip.remove(), 1800);
}
